import { FC, useState } from 'react'
import { Button, ConfirmBox } from '@corratech/pylot-ui'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useDeleteWishlist } from '../Hooks/use-delete-wishlist'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { useWishlist } from '../Hooks/use-wishlist'
import type { Wishlist } from '@pylot-data/pylotschema'

export type Props = {
    wishlist: Wishlist
}

export const DeleteWishlist: FC<Props> = ({ wishlist }) => {
    const { t } = useTranslation(['wishlist'])
    const router = useRouter()
    const [show, setShow] = useState(false)
    const { openToast } = useUI()
    const { deleteWishlist, isLoading } = useDeleteWishlist()
    const { wishlists } = useWishlist({
        revalidateOnFocus: false,
        revalidateOnMount: false
    })
    const { id, name } = wishlist
    const listIndex = wishlists?.findIndex((list) => list?.id === wishlist?.id)

    const openConfirm = () => setShow(true)
    const closeConfirm = () => setShow(false)

    const handleClick = async () => {
        closeConfirm()
        try {
            const response = await deleteWishlist({ id: Number(id) })

            if (response?.errors) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }

            if (response?.data?.deleteWishlist?.status) {
                router.push('/account/wishlist') //redirect to default wishlist
                openToast(
                    t(`wishlist|Wish List "{{ name }}" has been deleted.`, {
                        name
                    }),
                    ToastType.Success
                )
            }
        } catch (e) {
            if (e) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }
        }
    }

    //default wishlist cannot be deleted
    if (listIndex === 0) return null

    return (
        <>
            <Button
                loading={isLoading}
                disabled={isLoading}
                variant="secondary"
                onClick={openConfirm}
                onKeyUp={openConfirm}
            >
                {t('wishlist|Delete Wish List')}
            </Button>
            <ConfirmBox
                show={show}
                closeConfirm={closeConfirm}
                handleAction={handleClick}
            >
                {t(
                    `wishlist|Are you sure you want to delete your wish list '{{ name }}'? This action can't be undone.`,
                    { name }
                )}
            </ConfirmBox>
        </>
    )
}
